<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiActionSheet - Design System Action Sheet Component
 *
 * A mobile-friendly bottom sheet for presenting a list of actions.
 *
 * WHEN TO USE ACTIONSHEET (vs Modal vs Drawer):
 * - Quick action selection on mobile (share, delete, edit)
 * - Context menus on touch devices
 * - Simple choices with 2-5 options
 * - Destructive action confirmations on mobile
 *
 * DON'T USE ACTIONSHEET FOR:
 * - Desktop interfaces (use UiDropdownMenu instead)
 * - Complex forms or content (use Modal or Drawer)
 * - More than 5-6 actions (use Drawer)
 *
 * @example
 * const actions = [
 *   { id: 'edit', label: 'Edit', icon: 'ph:pencil' },
 *   { id: 'share', label: 'Share', icon: 'ph:share' },
 *   { id: 'delete', label: 'Delete', icon: 'ph:trash', destructive: true },
 * ];
 * <UiActionSheet v-model="isOpen" :actions="actions" @select="handleSelect" />
 */
import { onMounted, onUnmounted, watch } from 'vue';

export interface ActionSheetItem {
   /** Unique identifier */
   id: string;
   /** Display label */
   label: string;
   /** Optional icon */
   icon?: string;
   /** Whether this is a destructive action (shown in red) */
   destructive?: boolean;
   /** Whether this action is disabled */
   disabled?: boolean;
}

export interface UiActionSheetProps {
   /** Title of the action sheet */
   title?: string;
   /** Description/message */
   message?: string;
   /** List of actions */
   actions: ActionSheetItem[];
   /** Whether to show the cancel button */
   showCancel?: boolean;
   /** Cancel button text */
   cancelText?: string;
}

withDefaults(defineProps<UiActionSheetProps>(), {
   showCancel: true,
   cancelText: 'Cancel',
});

const emit = defineEmits<{
   select: [action: ActionSheetItem];
   cancel: [];
}>();

const modelValue = defineModel<boolean>({ default: false });

function selectAction(action: ActionSheetItem) {
   if (!action.disabled) {
      emit('select', action);
      modelValue.value = false;
   }
}

function cancel() {
   emit('cancel');
   modelValue.value = false;
}

function handleBackdropClick() {
   cancel();
}

function handleEscape(event: KeyboardEvent) {
   if (event.key === 'Escape' && modelValue.value) {
      cancel();
   }
}

// Lock body scroll when open
watch(modelValue, (isOpen) => {
   if (isOpen) {
      document.body.style.overflow = 'hidden';
   }
   else {
      document.body.style.overflow = '';
   }
});

onMounted(() => {
   document.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
   document.removeEventListener('keydown', handleEscape);
   document.body.style.overflow = '';
});
</script>

<template>
   <Teleport to="body">
      <!-- Backdrop -->
      <Transition
         enter-active-class="duration-300 ease-out"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="modelValue"
            class="fixed inset-0 bg-black/50 z-[9998]"
            @click="handleBackdropClick"
         />
      </Transition>

      <!-- Action Sheet -->
      <div
         class="fixed bottom-0 inset-x-0 z-[9999] px-2 pb-2 transition-transform duration-300 ease-out"
         :class="modelValue ? 'translate-y-0' : 'translate-y-full'"
      >
         <!-- Main actions card -->
         <div class="bg-white/95 backdrop-blur-lg rounded-2xl overflow-hidden mb-2">
            <!-- Header (optional) -->
            <div v-if="title || message" class="px-4 py-3 text-center border-b border-black/10">
               <h3 v-if="title" class="text-sm font-semibold text-black/60">
                  {{ title }}
               </h3>
               <p v-if="message" class="text-xs text-black/50 mt-0.5">
                  {{ message }}
               </p>
            </div>

            <!-- Actions -->
            <div class="flex flex-col">
               <button
                  v-for="(action, index) in actions"
                  :key="action.id"
                  type="button"
                  class="flex items-center justify-center gap-2 px-4 py-3.5 text-lg font-medium
              transition-colors active:bg-black/5"
                  :class="[
                     action.destructive ? 'text-red-500' : 'text-primary',
                     action.disabled ? 'opacity-40 cursor-not-allowed' : '',
                     index > 0 ? 'border-t border-black/10' : '',
                  ]"
                  :disabled="action.disabled"
                  @click="selectAction(action)"
               >
                  <Icon v-if="action.icon" :icon="action.icon" class="size-5" />
                  {{ action.label }}
               </button>
            </div>
         </div>

         <!-- Cancel button (separate card) -->
         <div v-if="showCancel" class="bg-white rounded-2xl overflow-hidden">
            <button
               type="button"
               class="w-full px-4 py-3.5 text-lg font-semibold text-primary
            transition-colors active:bg-black/5"
               @click="cancel"
            >
               {{ cancelText }}
            </button>
         </div>
      </div>
   </Teleport>
</template>
